import React, { useState, useEffect } from "react";
import Select from "react-select/creatable";

// Custom styles for React Select to match the form design
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: "2px solid #000000",
    borderRadius: "0",
    boxShadow: state.isFocused ? "0 0 0 1px #4f46e5" : "none",
    "&:hover": {
      border: "2px solid #4f46e5",
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#e0e7ff",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#3730a3",
    fontWeight: "500",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#3730a3",
    "&:hover": {
      backgroundColor: "#c7d2fe",
      color: "#1e1b4b",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
      ? "#e0e7ff"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    "&:hover": {
      backgroundColor: state.isSelected ? "#4f46e5" : "#e0e7ff",
    },
  }),
  menu: (provided) => ({
    ...provided,
    border: "2px solid #000000",
    borderRadius: "0",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
};

const defaultFields = {
  prerequisites: "",
  learningObjectives: "",
  targetAudience: "",
  categories: "",
  tags: "",
  thumbnail: null,
};

const MoreInfoModal = ({
  courseId,
  onSubmit,
  onBack,
  initialFields,
  submitButtonLabel,
}) => {
  const [fields, setFields] = useState(initialFields || defaultFields);
  const [tagInput, setTagInput] = useState("");
  const [preview, setPreview] = useState(null);

  // Category options for dropdown
  const [categoryOptions, setCategoryOptions] = useState([
    { value: "programming", label: "Programming" },
    { value: "webdev", label: "Web Development" },
    { value: "datasci", label: "Data Science" },
    { value: "softskills", label: "Soft Skills" },
    { value: "competitive", label: "Competitive Exams" },
    { value: "mobile", label: "Mobile Development" },
    { value: "ai", label: "Artificial Intelligence" },
    { value: "cybersecurity", label: "Cybersecurity" },
    { value: "cloud", label: "Cloud Computing" },
    { value: "devops", label: "DevOps" },
  ]);

  // Parse categories from string to array for Select component
  const parseCategories = (categoriesString) => {
    if (!categoriesString) return [];
    return categoriesString
      .split(",")
      .map((cat) => cat.trim())
      .filter(Boolean)
      .map((cat) => ({
        value: cat.toLowerCase(),
        label: cat,
      }));
  };

  // Convert selected categories back to string
  const formatCategories = (selectedCategories) => {
    return selectedCategories
      ? selectedCategories.map((cat) => cat.label).join(", ")
      : "";
  };

  // Handle category selection change
  const handleCategoryChange = (selected) => {
    const categoriesString = formatCategories(selected);
    setFields((prev) => ({ ...prev, categories: categoriesString }));
  };

  // Handle creating new category
  const handleCreateCategory = (inputValue) => {
    const newOption = { value: inputValue.toLowerCase(), label: inputValue };
    setCategoryOptions((prev) => [...prev, newOption]);
    const currentCategories = parseCategories(fields.categories);
    const updatedCategories = [...currentCategories, newOption];
    const categoriesString = formatCategories(updatedCategories);
    setFields((prev) => ({ ...prev, categories: categoriesString }));
  };

  useEffect(() => {
    if (initialFields) {
      setFields({ ...defaultFields, ...initialFields });
      setPreview(initialFields.thumbnailUrl || null);
    }
  }, [initialFields]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "thumbnail" && files && files[0]) {
      setFields((prev) => ({ ...prev, thumbnail: files[0] }));
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFields((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleTagInput = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === "," || e.key === "Enter") && tagInput.trim()) {
      e.preventDefault();
      const tagsArr = fields.tags
        ? fields.tags.split(",").map((t) => t.trim())
        : [];
      if (!tagsArr.includes(tagInput.trim())) {
        setFields((prev) => ({
          ...prev,
          tags: tagsArr.concat(tagInput.trim()).join(","),
        }));
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    const tagsArr = fields.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    setFields((prev) => ({
      ...prev,
      tags: tagsArr.filter((t) => t !== tag).join(","),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(fields);
  };

  const tagsArr = fields.tags
    ? fields.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200">
        <h3 className="text-2xl font-bold text-indigo-700 mb-6">
          More Course Information
        </h3>
        <form
          onSubmit={handleSubmit}
          className="space-y-8 more-info-modal-form"
        >
          {/* Advanced Info */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">
              Advanced Info
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Prerequisites
                </label>
                <textarea
                  name="prerequisites"
                  placeholder="e.g. Basic computer knowledge"
                  value={fields.prerequisites}
                  onChange={handleChange}
                  rows="2"
                  className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Learning Objectives
                </label>
                <textarea
                  name="learningObjectives"
                  placeholder="e.g. Understand Python basics, write simple scripts..."
                  value={fields.learningObjectives}
                  onChange={handleChange}
                  rows="2"
                  className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Target Audience
                </label>
                <textarea
                  name="targetAudience"
                  placeholder="e.g. Beginners, students, professionals..."
                  value={fields.targetAudience}
                  onChange={handleChange}
                  rows="2"
                  className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Categories
                  </label>
                  <Select
                    options={categoryOptions}
                    isMulti
                    isClearable
                    isSearchable
                    placeholder="Select existing categories or type to add new ones..."
                    onChange={handleCategoryChange}
                    onCreateOption={handleCreateCategory}
                    value={parseCategories(fields.categories)}
                    styles={selectStyles}
                    formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    isValidNewOption={(inputValue) =>
                      inputValue.length > 0 && inputValue.length <= 50
                    }
                    noOptionsMessage={() =>
                      "No categories found. Type to create a new one."
                    }
                    loadingMessage={() => "Loading categories..."}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Select from existing categories or type to create new ones.
                    You can select multiple categories.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-1">
                    {tagsArr.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium"
                      >
                        {tag}
                        <button
                          type="button"
                          className="ml-1 text-indigo-500 hover:text-red-500"
                          onClick={() => handleRemoveTag(tag)}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    name="tagInput"
                    placeholder="Type a tag and press Enter or ,"
                    value={tagInput}
                    onChange={handleTagInput}
                    onKeyDown={handleTagKeyDown}
                    className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                  />
                </div>
              </div>
            </div>
          </div>
          <hr className="my-2 border-gray-200" />
          {/* Media */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Media</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Thumbnail
                </label>
                <input
                  type="file"
                  name="thumbnail"
                  onChange={handleChange}
                  accept="image/*"
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 pl-4 py-2 tracking-wide text-lg"
                />
                {preview &&
                  // Only show preview if it's a blob: URL or a backend file URL (not the upload endpoint)
                  !preview.includes("/api/upload/image") && (
                    <img
                      src={
                        preview.startsWith("blob:")
                          ? preview
                          : preview.startsWith("http")
                          ? preview
                          : `${
                              process.env.REACT_APP_API_URL?.replace(
                                "/api",
                                ""
                              ) || "http://localhost:8000"
                            }${preview.startsWith("/") ? "" : "/"}${preview}`
                      }
                      alt="Thumbnail Preview"
                      className="mt-2 h-32 rounded shadow border object-contain"
                    />
                  )}
              </div>
            </div>
          </div>
          <div className="flex justify-between space-x-3 mt-8">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back
              </button>
            )}
            <button
              type="submit"
              className="px-6 py-2 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              {submitButtonLabel || "Save & Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MoreInfoModal;
